/**
 * SetupModal — UNIFIED modal for install, reinstall and create agent
 * 
 * mode='install': POST /checkout/order/:id/setup (new machine after payment)
 * mode='reinstall': POST /cloud/reinstallation-requests (reinstall existing machine)
 * mode='create': POST /cloud/machines/:id/agent-requests (create agent on existing machine)
 * 
 * Brain mode (clean/custom) + full Skills system
 */
import { useState, useEffect, useRef } from 'preact/hooks';
import { api } from '../api/client';
import { showToast } from './Toast';
import { T as Tokens } from '../styles/tokens';
import { SkillBadge, ApiKeyIndicator } from './skills-center/SkillBadge';
import { useSkillsMeta, enrichSkill } from './skills-center/useSkillsMeta';
import { SkeletonBar, Button } from './ui';

const T = {
  bg: '#0B0F15',
  card: Tokens.bgCard,
  text: '#E8EEF9',
  muted: '#A7B3C7',
  dimmed: '#6B7A90',
  accent: '#A3B1C6',
  success: Tokens.success,
  error: Tokens.danger,
  borderSolid: 'rgba(255,255,255,0.06)',
  font: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", sans-serif',
  gradient: 'linear-gradient(135deg, #2F6BFF, #8B5CF6)',
};

interface Order {
  id: string;
  plan_name: string;
  max_agents: number;
  plan_bonus: number;
  amount?: number;
}

export interface SetupModalProps {
  mode?: 'install' | 'reinstall' | 'create' | 'workspace';
  // install mode
  order?: Order;
  orderId?: string;
  planName?: string;
  // reinstall mode
  machineId?: string;
  machineName?: string;
  // create mode
  runnerId?: string;
  // common
  onClose: () => void;
  onSuccess: () => void;
}

interface Skill {
  id: string;
  name: string;
  slug?: string;
  description?: string;
  category?: string;
}

interface BrainTemplate {
  id: string;
  name: string;
  description?: string;
}

export function SetupModal({ mode = 'install', order, orderId, planName, machineId, machineName, runnerId, onClose, onSuccess }: SetupModalProps) {
  const resolvedOrderId = orderId || order?.id || '';
  const resolvedPlanName = planName || order?.plan_name || '';
  const [mounted, setMounted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userPlan, setUserPlan] = useState<string>('free');
  const [errorMsg, setErrorMsg] = useState('');
  const [success_, setSuccess] = useState(false);
  const [autoCloseCountdown, setAutoCloseCountdown] = useState(10);

  // Workspace state (mode='workspace')
  const [workspaceName, setWorkspaceName] = useState('');
  const [workspaceImage, setWorkspaceImage] = useState('');

  // Form
  const [agentName, setAgentName] = useState('');
  const [brainMode, setBrainMode] = useState<'clean' | 'custom'>('clean');
  const brainSectionRef = useRef<HTMLDivElement>(null);
  const [selectedBrain, setSelectedBrain] = useState('');
  const [brainSearch, setBrainSearch] = useState('');
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [telegramEnabled, setTelegramEnabled] = useState(false);
  const [telegramToken, setTelegramToken] = useState('');

  // Skills state
  const [skills, setSkills] = useState<Skill[]>([]);
  const [skillsLoading, setSkillsLoading] = useState(true);
  const [favoriteSkills, setFavoriteSkills] = useState<string[]>([]);
  const [skillsCategories, setSkillsCategories] = useState<string[]>([]);
  const [skillsOpen, setSkillsOpen] = useState(false);
  const [skillsTab, setSkillsTab] = useState<'all' | 'selected' | 'favorites' | 'category'>('all');
  const [skillsSelectedCategory, setSkillsSelectedCategory] = useState('');
  const [skillsSearch, setSkillsSearch] = useState('');

  // Brain state
  const [brains, setBrains] = useState<BrainTemplate[]>([]);
  const [brainsLoading, setBrainsLoading] = useState(true);

  const { meta: skillsMeta } = useSkillsMeta();

  useEffect(() => { requestAnimationFrame(() => setMounted(true)); }, []);

  // Auto-close countdown when success
  useEffect(() => {
    if (!success_) return;
    setAutoCloseCountdown(10);
    const iv = setInterval(() => {
      setAutoCloseCountdown(prev => {
        if (prev <= 1) { clearInterval(iv); onSuccess(); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [success_]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape' && !loading) onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [loading, onClose]);

  // Load skills
  useEffect(() => {
    Promise.all([
      api.get<{ skills: Skill[] }>('/cloud/all-skills'),
      api.get<{ skills: Skill[] }>('/cloud/user/skill-favorites'),
      api.get<{ categories: string[] }>('/cloud/user/skill-categories'),
    ])
      .then(([allRes, favRes, catRes]) => {
        const skillList = allRes?.skills || [];
        const sorted = skillList.sort((a, b) => {
          const catCmp = (a.category || 'Geral').localeCompare(b.category || 'Geral');
          if (catCmp !== 0) return catCmp;
          return (a.name || '').localeCompare(b.name || '');
        });
        setSkills(sorted);
        setFavoriteSkills((favRes?.skills || []).map(s => s.id));
        const cats = catRes?.categories || Array.from(new Set(sorted.map(s => s.category || 'Geral'))).sort();
        setSkillsCategories(cats);
        if (cats.length > 0) setSkillsSelectedCategory(cats[0]!);
      })
      .catch(() => {
        setSkills([]);
        setSkillsCategories([]);
        setFavoriteSkills([]);
      })
      .finally(() => setSkillsLoading(false));
  }, []);

  // Load brains
  useEffect(() => {
    api.get<{ cerebros: BrainTemplate[] }>('/cloud/cerebros')
      .then((res) => {
        setBrains(res?.cerebros || []);
      })
      .catch(() => setBrains([]))
      .finally(() => setBrainsLoading(false));
  }, []);

  // Auto-load docker image for workspace mode + fetch user plan
  useEffect(() => {
    if (mode === 'workspace') {
      api.get<any>('/cloud/workspaces/docker-images').then(res => {
        const imgs = res.images || [];
        if (imgs.length > 0) setWorkspaceImage(imgs[0].full);
      }).catch(() => {});
      
      // Fetch user plan
      api.getQuiet('/subscription/me').then((res: any) => {
        const plan = res?.subscription?.plan?.toLowerCase() || 'free';
        setUserPlan(plan === 'starter' ? 'free' : plan);
      }).catch(() => {});
    }
  }, [mode]);

  const toggleSkill = (id: string) => {
    setSelectedSkills(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  const submit = async () => {
    if (mode === 'workspace' && !workspaceName.trim()) { setErrorMsg('Nome do workspace é obrigatório'); return; }
    if (!agentName.trim()) { setErrorMsg('Nome do agente é obrigatório'); return; }
    if (brainMode === 'custom' && !selectedBrain) { setErrorMsg('Selecione um cérebro'); return; }

    setLoading(true);
    setErrorMsg('');
    try {
      if (mode === 'install') {
        await api.post(`/checkout/order/${resolvedOrderId}/setup`, {
          agentName: agentName.trim(),
          cerebroId: brainMode === 'clean' ? 'clean' : selectedBrain,
          skills: selectedSkills,
          telegramToken: telegramEnabled ? telegramToken : undefined,
        }, { timeout: 30000 });
        setSuccess(true);
      } else if (mode === 'reinstall') {
        await api.post('/cloud/reinstallation-requests', {
          machine_id: machineId,
          agent_name: agentName.trim(),
          brain_id: brainMode === 'clean' ? null : selectedBrain,
          skills: selectedSkills,
          telegram_token: telegramEnabled ? telegramToken : undefined,
        }, { timeout: 30000 });
        showToast('Pedido de reinstalação criado com sucesso!', 'success');
        onSuccess();
      } else if (mode === 'workspace') {
        await api.post('/cloud/workspaces', {
          name: workspaceName.trim(),
          image: workspaceImage || 'ghcr.io/elontools/openclaw:latest', /* tenant-id: docker image */
          plan: userPlan || 'free',
          agent: {
            name: agentName.trim(),
            brain: brainMode === 'clean' ? 'clean' : selectedBrain,
            skills: selectedSkills,
            telegram_token: telegramEnabled ? telegramToken.trim() : undefined,
          },
        }, { timeout: 30000 });
        showToast('Ambiente criado! Estará pronto em alguns minutos.', 'success');
        onSuccess();
      } else {
        // create mode
        const body: any = {
          agent_name: agentName.trim(),
          brain_files: brainMode === 'clean' ? ['soul', 'user', 'memory', 'agents', 'tools', 'heartbeat', 'identity', 'bootstrap'] : undefined,
        };
        if (brainMode === 'custom' && selectedBrain) body.brain_id = selectedBrain;
        if (selectedSkills.length > 0) body.skills = selectedSkills;
        if (telegramEnabled && telegramToken.trim()) body.telegram_token = telegramToken.trim();
        await api.post(`/cloud/machines/${machineId}/agent-requests`, body, { timeout: 30000 });
        showToast('Agente criado com sucesso!', 'success');
        onSuccess();
      }
    } catch (e: any) {
      setErrorMsg(e.message || 'Erro ao processar');
    } finally {
      setLoading(false);
    }
  };

  const neutralTab = (active: boolean) => ({
    background: active ? 'rgba(163,177,198,0.12)' : 'rgba(255,255,255,0.02)',
    color: active ? T.text : T.dimmed,
    border: active ? '1.5px solid rgba(163,177,198,0.25)' : `1px solid ${T.borderSolid}`,
  });

  const inputStyle: any = {
    width: '100%', padding: '10px 14px', borderRadius: 10, border: `1px solid ${T.borderSolid}`,
    background: 'rgba(255,255,255,0.03)', color: T.text, fontSize: 13,
    fontFamily: T.font, outline: 'none', boxSizing: 'border-box',
  };

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget && !loading) onClose(); }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
        backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
        zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
        opacity: mounted ? 1 : 0, transition: 'opacity 0.2s',
      }}
    >
      <div style={{
        background: 'rgba(11,15,21,0.95)', backdropFilter: 'blur(20px)',
        borderRadius: 20, width: '100%', maxWidth: 540,
        maxHeight: '85vh', display: 'flex', flexDirection: 'column',
        border: `1px solid ${T.borderSolid}`,
        boxShadow: '0 25px 60px rgba(0,0,0,0.5)',
        transform: mounted ? 'scale(1) translateY(0)' : 'scale(0.92) translateY(10px)',
        transition: 'transform 0.25s', fontFamily: T.font, overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '24px 28px 16px', borderBottom: `1px solid ${T.borderSolid}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0,
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 19, fontWeight: 800, color: T.text }}>
              {mode === 'workspace' ? 'Criar Novo Ambiente' : mode === 'install' ? 'Configure seu Agente' : mode === 'reinstall' ? 'Reinstalar Máquina' : 'Criar Novo Agente'}
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: T.muted }}>
              {mode === 'workspace' ? 'Configure o workspace e o agente' : mode === 'install' ? `Plano ${resolvedPlanName}` : mode === 'reinstall' ? (machineName || '') : 'Configure o agente antes de criar'}
            </p>
          </div>
          <Button variant="ghost" onClick={onClose} disabled={loading} style={{
            background: 'none', border: 'none', color: T.dimmed, fontSize: 22,
            cursor: loading ? 'not-allowed' : 'pointer', padding: 4, lineHeight: 1,
          }}></Button>
        </div>

        {/* Success state */}
        {success_ ? (
          <div style={{ padding: '48px 24px', textAlign: 'center' }}>
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" style={{ margin: '0 auto 16px' }}>
              <circle cx="32" cy="32" r="30" stroke={T.success} strokeWidth="3" fill="rgba(16,185,129,0.1)" />
              <path d="M20 32l8 8 16-16" stroke={T.success} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <div style={{ fontSize: 20, fontWeight: 700, color: T.text, marginBottom: 8 }}>
              Configuracao iniciada!
            </div>
            <div style={{ fontSize: 14, color: T.muted, marginBottom: 24 }}>
              Seu agente estara pronto em 10-30 minutos
            </div>
            <Button variant="ghost"
              onClick={onSuccess}
              style={{
                padding: '12px 28px', borderRadius: 10, border: 'none',
                background: T.gradient, color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
              }}
            >
              Ver meus agentes
            </Button>
            <div style={{ fontSize: 12, color: T.dimmed, marginTop: 12 }}>
              Fechando em {autoCloseCountdown}s
            </div>
          </div>
        ) : (
          <>
          {/* Scrollable content */}
          <div style={{
            padding: '20px 28px 16px', display: 'flex', flexDirection: 'column', gap: 16,
            overflowY: 'auto', overflowX: 'hidden', flexGrow: 1, minHeight: 0,
          }}>
            {/* Workspace name (only in workspace mode) */}
            {mode === 'workspace' && (
              <FieldGroup label="Nome do Workspace">
                <input
                  type="text" value={workspaceName}
                  onInput={(e) => setWorkspaceName((e.target as HTMLInputElement).value)}
                  placeholder="Ex: Producao, Vendas, Suporte"
                  style={inputStyle}
                />
              </FieldGroup>
            )}

            {/* Agent name */}
            <FieldGroup label="Nome do Agente">
              <input
                type="text" value={agentName}
                onInput={(e) => setAgentName((e.target as HTMLInputElement).value)}
                placeholder="Meu Agente"
                style={inputStyle}
              />
            </FieldGroup>

            {/* Brain mode */}
            <FieldGroup label="Cerebro do Agente" desc="Escolha como configurar a inteligencia">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Button variant="ghost"
                  onClick={() => { setBrainMode('clean'); setSelectedBrain(''); }}
                  style={{
                    padding: '14px 12px', borderRadius: 12, cursor: 'pointer',
                    background: brainMode === 'clean' ? 'rgba(163,177,198,0.1)' : 'rgba(255,255,255,0.02)',
                    border: `2px solid ${brainMode === 'clean' ? 'rgba(163,177,198,0.3)' : T.borderSolid}`,
                    color: T.text, fontSize: 13, fontWeight: 600, fontFamily: T.font,
                    textAlign: 'center', transition: 'all 0.15s',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                  }}
                >
 <span style={{ fontSize: 20 }}></span>
                  <div>Limpo</div>
                  <div style={{ fontSize: 10, color: T.dimmed, fontWeight: 400 }}>Do zero</div>
                </Button>
                <Button variant="ghost"
                  onClick={() => { setBrainMode('custom'); setTimeout(() => brainSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100); }}
                  style={{
                    padding: '14px 12px', borderRadius: 12, cursor: 'pointer',
                    background: brainMode === 'custom' ? 'rgba(163,177,198,0.1)' : 'rgba(255,255,255,0.02)',
                    border: `2px solid ${brainMode === 'custom' ? 'rgba(163,177,198,0.3)' : T.borderSolid}`,
                    color: T.text, fontSize: 13, fontWeight: 600, fontFamily: T.font,
                    textAlign: 'center', transition: 'all 0.15s',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                  }}
                >
 <span style={{ fontSize: 20 }}></span>
                  <div>Personalizado</div>
                  <div style={{ fontSize: 10, color: T.dimmed, fontWeight: 400 }}>Com config</div>
                </Button>
              </div>

              {brainMode === 'custom' && (
                <div ref={brainSectionRef} style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {brainsLoading ? (
                    <div style={{ padding: '8px 0' }}><SkeletonBar width="50%" height={12} /></div>
                  ) : brains.length === 0 ? (
                    <div style={{ fontSize: 12, color: T.dimmed, padding: '12px', textAlign: 'center', borderRadius: 10, border: `1px dashed ${T.borderSolid}` }}>
                      Nenhum cérebro personalizado disponível. Use "Limpo".
                    </div>
                  ) : (
                    <>
                      {/* Search */}
                      <input
                        type="text"
                        placeholder="Buscar cérebro..."
                        value={brainSearch}
                        onInput={(e) => setBrainSearch((e.target as HTMLInputElement).value.toLowerCase())}
                        style={{
                          width: '100%', padding: '9px 12px', borderRadius: 10,
                          border: `1px solid ${T.borderSolid}`, background: 'rgba(255,255,255,0.03)',
                          color: T.text, fontSize: 12, fontFamily: T.font, outline: 'none', boxSizing: 'border-box',
                        }}
                      />
                      {/* Count */}
                      <div style={{ fontSize: 11, color: T.dimmed }}>
                        {brains.filter(b => !brainSearch || b.name.toLowerCase().includes(brainSearch) || (b.description || '').toLowerCase().includes(brainSearch)).length} de {brains.length} cérebros
                        {selectedBrain && (() => {
                          const sel = brains.find(b => b.id === selectedBrain);
                          return sel ? <span style={{ color: T.accent }}> · Selecionado: {sel.name}</span> : null;
                        })()}
                      </div>
                      {/* List with max-height scroll */}
                      <div style={{
                        maxHeight: 240, overflowY: 'auto', overflowX: 'hidden',
                        display: 'flex', flexDirection: 'column', gap: 6,
                        scrollbarWidth: 'thin', scrollbarColor: 'rgba(163,177,198,0.2) transparent',
                      }}>
                        {brains
                          .filter(b => !brainSearch || b.name.toLowerCase().includes(brainSearch) || (b.description || '').toLowerCase().includes(brainSearch))
                          .map(brain => (
                          <label key={brain.id} style={{
                            display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px',
                            borderRadius: 10, cursor: 'pointer',
                            background: selectedBrain === brain.id ? 'rgba(139,92,246,0.08)' : 'rgba(255,255,255,0.02)',
                            border: `1.5px solid ${selectedBrain === brain.id ? 'rgba(139,92,246,0.3)' : T.borderSolid}`,
                            transition: 'all 0.15s',
                          }}>
                            <input
                              type="radio" name="brain" value={brain.id}
                              checked={selectedBrain === brain.id}
                              onChange={() => setSelectedBrain(brain.id)}
                              style={{ accentColor: T.purple, flexShrink: 0, marginTop: 2 }}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{brain.name}</div>
                              {brain.description && (
                                <div style={{ fontSize: 11, color: T.dimmed, marginTop: 2, lineHeight: 1.4 }}>{brain.description}</div>
                              )}
                            </div>
                          </label>
                        ))}
                        {brains.filter(b => !brainSearch || b.name.toLowerCase().includes(brainSearch) || (b.description || '').toLowerCase().includes(brainSearch)).length === 0 && (
                          <div style={{ fontSize: 12, color: T.dimmed, textAlign: 'center', padding: '16px 0' }}>
                            Nenhum cérebro encontrado para "{brainSearch}"
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </FieldGroup>

            {/* Skills — Collapsible */}
            {!skillsLoading && skills.length > 0 && (
              <div style={{ flexShrink: 0 }}>
                <Button variant="ghost"
                  type="button"
                  onClick={() => setSkillsOpen(!skillsOpen)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '12px 0', background: 'none', border: 'none', cursor: 'pointer', color: T.text,
                  }}
                >
                  <div>
                    <span style={{ fontSize: 13, fontWeight: 700 }}>Skills</span>
                    <span style={{ fontSize: 11, color: T.dimmed, marginLeft: 8 }}>
                      {(() => {
                        const vCount = selectedSkills.filter(id => { const s = skills.find(sk => sk.id === id); return s && enrichSkill(s.name, skillsMeta).tier === 'tier1'; }).length;
                        const rCount = selectedSkills.filter(id => { const s = skills.find(sk => sk.id === id); return s && enrichSkill(s.name, skillsMeta).tier === 'tier2'; }).length;
                        return `${vCount} verificadas · ${rCount} recomendadas · ${selectedSkills.length} total`;
                      })()}
                    </span>
                  </div>
                  <span style={{ fontSize: 12, color: T.dimmed, transition: 'transform .2s', transform: skillsOpen ? 'rotate(180deg)' : 'none' }}>▼</span>
                </Button>

                {skillsOpen && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {/* Header actions */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                      <Button variant="ghost"
                        onClick={() => {
                          const tier1Ids = skills.filter(s => { const m = enrichSkill(s.name, skillsMeta); return m.tier === 'tier1' || m.badge === 'verified'; }).map(s => s.id);
                          setSelectedSkills(prev => [...new Set([...prev, ...tier1Ids])]);
                        }}
                        style={{
                          fontSize: 11, fontWeight: 600, padding: '6px 14px', borderRadius: 8,
                          border: '1px solid rgba(16,185,129,0.3)', background: 'rgba(16,185,129,0.08)', color: T.success,
                          cursor: 'pointer', whiteSpace: 'nowrap',
                        }}
                      >Selecionar Todas Verificadas</Button>
                    </div>

                    {/* Verified skills section */}
                    {(() => {
                      const verifiedSkills = skills.filter(s => { const m = enrichSkill(s.name, skillsMeta); return m.tier === 'tier1' || m.badge === 'verified'; });
                      if (verifiedSkills.length === 0) return null;
                      return (
                        <div style={{ padding: 12, borderRadius: 10, background: 'rgba(16,185,129,0.04)', border: '1px solid rgba(16,185,129,0.2)', marginBottom: 8 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: T.success, marginBottom: 8 }}>Verificadas por WhiteLabelClaw ({verifiedSkills.length})</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {verifiedSkills.map(s => (
                              <Button variant="ghost" key={s.id} onClick={() => toggleSkill(s.id)} style={{
                                padding: '4px 10px', borderRadius: 6, fontSize: 10, fontWeight: 600, cursor: 'pointer',
                                background: selectedSkills.includes(s.id) ? 'rgba(16,185,129,0.15)' : 'rgba(255,255,255,0.04)',
                                border: `1px solid ${selectedSkills.includes(s.id) ? 'rgba(16,185,129,0.3)' : T.borderSolid}`,
                                color: selectedSkills.includes(s.id) ? T.success : T.muted,
                              }}>{selectedSkills.includes(s.id) ? '' : ''}{s.name}</Button>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Tabs */}
                    <div style={{
                      display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 8, marginBottom: 12,
                      scrollbarWidth: 'thin',
                    }}>
                      <Button variant="ghost"
                        onClick={() => { setSkillsTab('selected'); setSkillsSelectedCategory(''); setSkillsSearch(''); }}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          padding: '6px 14px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                          whiteSpace: 'nowrap', cursor: 'pointer', flexShrink: 0, transition: 'all 0.15s',
                          ...neutralTab(skillsTab === 'selected'),
                        }}
                      >
                        <span style={{ fontSize: 13 }}></span> Selecionadas
                        <span style={{ fontSize: 10, fontWeight: 700, background: 'rgba(163,177,198,0.08)', padding: '1px 6px', borderRadius: 8 }}>{selectedSkills.length}</span>
                      </Button>

                      <Button variant="ghost"
                        onClick={() => { setSkillsTab('favorites'); setSkillsSelectedCategory(''); setSkillsSearch(''); }}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          padding: '6px 14px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                          whiteSpace: 'nowrap', cursor: 'pointer', flexShrink: 0, transition: 'all 0.15s',
                          ...neutralTab(skillsTab === 'favorites'),
                        }}
                      >
                        Favoritos
                        <span style={{ fontSize: 10, fontWeight: 700, background: 'rgba(163,177,198,0.08)', padding: '1px 6px', borderRadius: 8 }}>{favoriteSkills.length}</span>
                      </Button>

                      <Button variant="ghost"
                        onClick={() => { setSkillsTab('all'); setSkillsSelectedCategory(''); setSkillsSearch(''); }}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          padding: '6px 14px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                          whiteSpace: 'nowrap', cursor: 'pointer', flexShrink: 0, transition: 'all 0.15s',
                          ...neutralTab(skillsTab === 'all'),
                        }}
                      >
                        Todos
                        <span style={{ fontSize: 10, fontWeight: 700, background: 'rgba(163,177,198,0.08)', padding: '1px 6px', borderRadius: 8 }}>{skills.length}</span>
                      </Button>

                      <div style={{ width: 1, height: 24, alignSelf: 'center', flexShrink: 0, background: T.borderSolid, margin: '0 2px' }} />

                      {skillsCategories.map(cat => {
                        const count = skills.filter(s => (s.category || 'Geral') === cat).length;
                        const isActive = skillsTab === 'category' && skillsSelectedCategory === cat;
                        return (
                          <Button variant="ghost" key={cat}
                            onClick={() => { setSkillsTab('category'); setSkillsSelectedCategory(cat); setSkillsSearch(''); }}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                              padding: '6px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                              whiteSpace: 'nowrap', cursor: 'pointer', flexShrink: 0, transition: 'all 0.15s',
                              ...neutralTab(isActive),
                            }}
                          >
                            {cat.charAt(0).toUpperCase() + cat.slice(1)}
                            <span style={{ fontSize: 10, fontWeight: 700, opacity: 0.7, background: 'rgba(163,177,198,0.06)', padding: '1px 5px', borderRadius: 8 }}>{count}</span>
                          </Button>
                        );
                      })}
                    </div>

                    {/* Search */}
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        type="text" placeholder="Buscar skills..."
                        value={skillsSearch}
                        onInput={(e) => setSkillsSearch((e.target as HTMLInputElement).value.toLowerCase())}
                        style={{
                          flex: 1, padding: '9px 12px', borderRadius: 10, border: `1px solid ${T.borderSolid}`,
                          background: 'rgba(255,255,255,0.02)', color: T.text, fontSize: 11,
                          fontFamily: T.font, outline: 'none', boxSizing: 'border-box',
                        }}
                      />
                    </div>

                    {/* Skills list */}
                    <div style={{ maxHeight: 280, overflowY: 'auto', overflowX: 'hidden', display: 'flex', flexDirection: 'column', gap: 7 }}>
                      {(() => {
                        const filtered = skills.filter(s => {
                          const matchSearch = skillsSearch === '' ||
                            s.name.toLowerCase().includes(skillsSearch) ||
                            (s.description && s.description.toLowerCase().includes(skillsSearch));
                          const matchTab = skillsTab === 'all' ? true :
                            skillsTab === 'selected' ? selectedSkills.includes(s.id) :
                            skillsTab === 'favorites' ? favoriteSkills.includes(s.id) :
                            skillsTab === 'category' ? (s.category || 'Geral') === skillsSelectedCategory :
                            false;
                          return matchSearch && matchTab;
                        });
                        if (filtered.length === 0) {
                          return <div style={{ fontSize: 11, color: T.dimmed, textAlign: 'center', padding: '24px 0' }}>Nenhuma skill encontrada</div>;
                        }
                        return filtered.map(skill => (
                          <div key={skill.id} style={{
                            display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                            borderRadius: 10,
                            background: selectedSkills.includes(skill.id) ? 'rgba(163,177,198,0.06)' : 'rgba(255,255,255,0.02)',
                            border: `1px solid ${selectedSkills.includes(skill.id) ? 'rgba(163,177,198,0.15)' : T.borderSolid}`,
                            transition: 'all 0.15s',
                          }}>
                            <div style={{ fontSize: 16, flexShrink: 0, width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
 {enrichSkill(skill.name, skillsMeta).emoji || ''}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 11, fontWeight: 600, color: T.text, marginBottom: 1, display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                                {skill.name}
                                {(() => { const m = enrichSkill(skill.name, skillsMeta); return <SkillBadge tier={m.tier} verified={m.verified} badge={m.badge} size="sm" />; })()}
                                {(() => { const m = enrichSkill(skill.name, skillsMeta); return m.requires_api_key ? <ApiKeyIndicator requiresApiKey size="sm" /> : null; })()}
                              </div>
                              <div style={{ fontSize: 10, color: T.dimmed, lineHeight: 1.3 }}>
                                {enrichSkill(skill.name, skillsMeta).description_pt || skill.description || 'Sem descricao'}
                              </div>
                            </div>
                            <Button variant="ghost"
                              onClick={() => toggleSkill(skill.id)}
                              style={{
                                padding: '5px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                                fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap',
                                background: selectedSkills.includes(skill.id) ? 'rgba(163,177,198,0.12)' : 'rgba(47,107,255,0.15)',
                                color: selectedSkills.includes(skill.id) ? T.accent : '#93B4FF',
                                transition: 'all 0.15s',
                              }}
                            >
                              {selectedSkills.includes(skill.id) ? 'Selecionada' : '+ Adicionar'}
                            </Button>
                            {skill.category && (
                              <span style={{
                                fontSize: 9, fontWeight: 600, color: T.dimmed,
                                background: 'rgba(163,177,198,0.06)', padding: '2px 6px', borderRadius: 6,
                                whiteSpace: 'nowrap', flexShrink: 0,
                              }}>{skill.category}</span>
                            )}
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Telegram toggle */}
            <div>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
                onClick={() => setTelegramEnabled(!telegramEnabled)}
              >
                <div style={{
                  width: 36, height: 20, borderRadius: 10,
                  background: telegramEnabled ? T.accent : 'rgba(255,255,255,0.1)',
                  position: 'relative', transition: 'background 0.2s',
                }}>
                  <div style={{
                    width: 16, height: 16, borderRadius: '50%', background: '#fff',
                    position: 'absolute', top: 2,
                    left: telegramEnabled ? 18 : 2,
                    transition: 'left 0.2s',
                  }} />
                </div>
                <span style={{ fontSize: 13, color: T.text, fontWeight: 600 }}>Telegram</span>
              </div>
              {telegramEnabled && (
                <input
                  type="text" value={telegramToken}
                  onInput={(e) => setTelegramToken((e.target as HTMLInputElement).value)}
                  placeholder="Bot token do Telegram"
                  style={{ ...inputStyle, marginTop: 8, fontSize: 13 }}
                />
              )}
            </div>

          </div>

          {/* ═══ FIXED FOOTER — always visible ═══ */}
          <div style={{
            padding: '16px 28px 24px', borderTop: `1px solid ${T.borderSolid}`,
            flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 10,
            background: 'rgba(11,15,21,0.98)',
          }}>
            {/* Error */}
            {errorMsg && (
              <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', color: T.error, fontSize: 13 }}>
                {errorMsg}
              </div>
            )}

            {/* Warning */}
            {mode === 'install' && (
              <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.15)', fontSize: 12, color: T.warning }}>
                <strong>Atenção:</strong> Seu agente será configurado e estará pronto em 10-30 minutos.
              </div>
            )}
            {mode === 'reinstall' && (
              <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)', fontSize: 12, color: '#FCA5A5' }}>
                <strong>Atenção:</strong> A reinstalação irá apagar todos os dados do agente atual.
              </div>
            )}

            {/* Recursos do Plano — Collapsible */}
            <PlanResourcesSection plan={resolvedPlanName === 'starter' ? 'free' : (resolvedPlanName || userPlan || 'free')} />

            {/* Submit */}
            <Button variant="ghost"
              onClick={submit}
              disabled={loading || !agentName.trim() || (mode === 'workspace' && !workspaceName.trim())}
              style={{
                width: '100%', padding: '14px 24px', borderRadius: 12, border: 'none',
                background: loading ? T.dimmed : T.gradient,
                color: '#fff', fontSize: 15, fontWeight: 700,
                cursor: loading ? 'wait' : 'pointer',
                opacity: (!agentName.trim() || (mode === 'workspace' && !workspaceName.trim())) ? 0.5 : 1,
                fontFamily: T.font, transition: 'all 0.2s',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                boxShadow: loading ? 'none' : '0 4px 20px rgba(47,107,255,0.3)',
              }}
            >
              {loading && (
                <span style={{
                  display: 'inline-block', width: 16, height: 16,
                  border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff',
                  borderRadius: '50%', animation: 'setupSpin 0.6s linear infinite',
                }} />
              )}
              {loading
                ? (mode === 'workspace' ? 'Criando Ambiente...' : mode === 'install' ? 'Configurando...' : mode === 'reinstall' ? 'Reinstalando...' : 'Criando...')
                : (mode === 'workspace' ? 'Criar Ambiente' : mode === 'install' ? 'Iniciar Configuração' : mode === 'reinstall' ? 'Executar Reinstalação' : 'Criar Agente')}
            </Button>
          </div>
        </>
        )}
      </div>

      <style>{`
        @keyframes setupSpin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

function PlanResourcesSection({ plan }: { plan: string }) {
  const [open, setOpen] = useState(true);
  const [config, setConfig] = useState<any>(null);

  useEffect(() => {
    const lookupPlan = (plan === 'starter' ? 'free' : plan) || 'free';
    api.get(`/cloud/workspaces/plan-config?plan=${lookupPlan}`).then((res: any) => {
      if (res?.config) setConfig(res.config);
    }).catch(() => {});
  }, [plan]);

  if (!config) return null;

  const isFree = (plan === 'free' || plan === 'starter' || !plan);
  const dailyLimit = config.daily_hours_limit || 0;

  const rowStyle = { display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '4px 0', color: '#A7B3C7' };
  const valStyle = { color: '#E8EEF9', fontWeight: 600 as const };

  // Funções de formatação
  function formatMemory(val: string): string {
    if (!val) return '-';
    const mi = parseInt(val);
    if (!isNaN(mi) && val.includes('Mi')) {
      return mi >= 1024 ? `${(mi/1024).toFixed(mi % 1024 === 0 ? 0 : 1)} GB` : `${mi} MB`;
    }
    if (val.includes('Gi')) return val.replace('Gi', ' GB');
    return val;
  }

  function formatCpu(val: string): string {
    if (!val) return '-';
    const m = parseInt(val);
    if (!isNaN(m) && val.includes('m')) {
      return m >= 1000 ? `${(m/1000).toFixed(0)} vCPU` : `${(m/1000).toFixed(1)} vCPU`;
    }
    return val;
  }

  function formatStorage(val: string): string {
    if (!val) return '-';
    if (val.includes('Gi')) return val.replace('Gi', ' GB');
    if (val.includes('Mi')) {
      const mi = parseInt(val);
      return mi >= 1024 ? `${(mi/1024).toFixed(0)} GB` : `${mi} MB`;
    }
    return val;
  }

  return (
    <div style={{ flexShrink: 0 }}>
      <Button variant="ghost"
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 0', background: 'none', border: 'none', cursor: 'pointer', color: '#E8EEF9',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700 }}>Recursos do Plano</span>
          <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: isFree ? 'rgba(107,114,128,0.3)' : 'rgba(59,130,246,0.3)', color: isFree ? T.textSecondary : '#93c5fd', fontWeight: 600 }}>
            {(plan || 'free').toUpperCase()}
          </span>
          <span style={{ fontSize: 11, color: '#6B7A90' }}>
            {dailyLimit > 0 ? `${dailyLimit}h/dia` : 'Ilimitado'}
          </span>
        </div>
        <span style={{ fontSize: 12, color: '#6B7A90', transition: 'transform .2s', transform: open ? 'rotate(180deg)' : 'none' }}>▼</span>
      </Button>

      {open && (
        <div style={{ padding: '8px 12px', background: 'rgba(255,255,255,0.02)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={rowStyle}><span>RAM</span><span style={valStyle}>{formatMemory(config.memory_request)} → {formatMemory(config.memory_limit)}</span></div>
          <div style={rowStyle}><span>CPU</span><span style={valStyle}>{formatCpu(config.cpu_request)} → {formatCpu(config.cpu_limit)}</span></div>
          <div style={rowStyle}><span>Storage</span><span style={valStyle}>{formatStorage(config.storage_size || config.storage_limit)}</span></div>
          <div style={rowStyle}><span>Réplicas</span><span style={valStyle}>{config.max_replicas || 1}</span></div>
          <div style={rowStyle}><span>Auto-Suspend</span><span style={valStyle}>{config.auto_suspend_enabled ? `${config.auto_suspend_minutes}min` : 'Sempre ligado'}</span></div>
          <div style={rowStyle}><span>Limite</span><span style={valStyle}>{dailyLimit > 0 ? `${dailyLimit}h/dia` : '∞ Ilimitado'}</span></div>

          {isFree && (
            <div style={{ marginTop: 8, padding: 8, background: 'rgba(245,158,11,0.08)', borderRadius: 6, border: '1px solid rgba(245,158,11,0.2)', fontSize: 11, color: T.warning }}>
              Upgrade para Pro para instância sempre ligada e uso ilimitado
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FieldGroup({ label, desc, children }: { label: string; desc?: string; children: any }) {
  return (
    <div style={{ flexShrink: 0 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#E8EEF9' }}>{label}</span>
        {desc && <span style={{ fontSize: 11, color: '#6B7A90' }}>{desc}</span>}
      </div>
      {children}
    </div>
  );
}
